import { memo } from 'react';
import type { ButtonProps } from '@/src/types';
import { cn } from '@/src/utils';

const variants = {
  primary:
    'bg-white text-black hover:bg-gray-200 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_40px_rgba(255,255,255,0.6)]',
  secondary:
    'bg-blue-600 text-white hover:bg-blue-500 shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_40px_rgba(37,99,235,0.6)]',
  ghost:
    'bg-transparent text-white border border-white/20 hover:bg-white/10 hover:border-white/50',
  outline:
    'bg-transparent text-white border border-white/10 hover:bg-white hover:text-black',
};

const sizes = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
};

function Button({
  variant = 'primary',
  size = 'md',
  children,
  className,
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  icon,
  iconPosition = 'right',
}: ButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        'group relative overflow-hidden rounded-lg font-semibold transition-all duration-300',
        'hover:scale-105 active:scale-95',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
        'flex items-center justify-center gap-2',
        variants[variant],
        sizes[size],
        className
      )}
    >
      {/* Shine effect for primary variant */}
      {variant === 'primary' && (
        <div className="absolute top-0 -left-[100%] w-full h-full bg-gradient-to-r from-transparent via-white/50 to-transparent skew-x-12 transition-all duration-700 group-hover:left-[100%]" />
      )}

      {/* Loading spinner */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-inherit">
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Content */}
      <span
        className={cn(
          'relative z-10 flex items-center gap-2',
          loading && 'opacity-0'
        )}
      >
        {icon && iconPosition === 'left' && (
          <span className="transition-transform group-hover:-translate-x-1">
            {icon}
          </span>
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <span className="transition-transform group-hover:translate-x-1">
            {icon}
          </span>
        )}
      </span>
    </button>
  );
}

export default memo(Button);
